import React, { useEffect, useRef } from 'react';

function NetworkBackground({ className = '', density = 1 }) {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let width = 0;
    let height = 0;
    const nodes = [];
    const nodeCount = Math.floor(58 * density);
    const linkDistance = 150;
    const maxSpeed = reducedMotion ? 0.06 : 0.32;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      width = parent.clientWidth;
      height = parent.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const seedNodes = () => {
      nodes.length = 0;
      for (let i = 0; i < nodeCount; i += 1) {
        nodes.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * maxSpeed,
          vy: (Math.random() - 0.5) * maxSpeed,
          radius: Math.random() * 1.4 + 0.9,
          pulse: Math.random() * Math.PI * 2,
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      for (const node of nodes) {
        node.x += node.vx;
        node.y += node.vy;
        node.pulse += reducedMotion ? 0.004 : 0.018;

        if (node.x <= 0 || node.x >= width) node.vx *= -1;
        if (node.y <= 0 || node.y >= height) node.vy *= -1;
        node.x = Math.max(0, Math.min(width, node.x));
        node.y = Math.max(0, Math.min(height, node.y));
      }

      for (let i = 0; i < nodes.length; i += 1) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j += 1) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist >= linkDistance) continue;

          const alpha = (1 - dist / linkDistance) * 0.28;
          const gradient = ctx.createLinearGradient(a.x, a.y, b.x, b.y);
          gradient.addColorStop(0, `rgba(34, 211, 238, ${alpha})`);
          gradient.addColorStop(1, `rgba(129, 140, 248, ${alpha * 0.85})`);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 0.75;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      for (const node of nodes) {
        const glow = 0.55 + Math.sin(node.pulse) * 0.25;
        const outer = node.radius + glow * 1.2;

        ctx.fillStyle = `rgba(34, 211, 238, ${0.08 + glow * 0.06})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, outer * 2.2, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(129, 140, 248, ${0.35 + glow * 0.25})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, outer, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(224, 242, 254, ${0.55 + glow * 0.35})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * 0.55, 0, Math.PI * 2);
        ctx.fill();
      }

      frameRef.current = requestAnimationFrame(draw);
    };

    resize();
    seedNodes();
    draw();

    const parent = canvas.parentElement;
    const observer = parent ? new ResizeObserver(() => {
      resize();
      seedNodes();
    }) : null;
    observer?.observe(parent);

    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(frameRef.current);
      } else {
        draw();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelAnimationFrame(frameRef.current);
      observer?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [density]);

  return (
    <div className={`network-background ${className}`.trim()} aria-hidden="true">
      <canvas ref={canvasRef} className="network-background-canvas" />
      <div className="network-background-vignette" />
      <div className="network-background-grid" />
    </div>
  );
}

export default NetworkBackground;
